import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Button } from '@osmosis/react-ui/components/Button';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Box } from '@osmosis/react-ui/components/Box';
import { useFetch } from '../../../../context/FetchProvider';
import { useQuizShortcutsContext } from '../../../../context/QuizShortcutsContext';
import { fetchRepetitionSettings } from '../../repetitionSetting/api';
import { ReverseQuizButtons } from '../utils';
import { useQuizContext } from '../../../../context/QuizContext';
import { QuizConfidence, RepetitionStats } from '../../../../types/quiz';
import { P } from '@osmosis/react-ui/components/Text';
import {
  shortcutsIds,
  SHORTCUTS_QUIZ_DEFAULT,
} from '../../../../constants/quizShortcuts';
import {
  RepetitionSettingsFormI,
  SettingType,
} from '../../../quiz/repetitionSetting/types';
import { useQuizItems } from '../../../../context/QuizItemsProvider';
import { QuizSettings } from '../../../../lib/shared/quiz/quizSetting';
import { Line } from 'recharts';

type FlashcardControl = 'incorrect' | 'correct';

const getRepetitionKey = (
  accuracy: 'correct' | 'incorrect',
  confidence: QuizConfidence,
): keyof RepetitionSettingsFormI | null => {
  switch (confidence) {
    case QuizConfidence.LOW:
      return `${accuracy}Low`;
    case QuizConfidence.MEDIUM:
      return `${accuracy}Med`;
    case QuizConfidence.HIGH:
      return `${accuracy}High`;
    default:
      return null;
  }
};

const FlashcardControlsItem = ({
  repetitionStats,
  repetitionValue,
  confidence,
  btnType,
  id,
  handleSubmit,
}: {
  repetitionStats: RepetitionStats;
  repetitionValue: string;
  confidence: QuizConfidence;
  btnType: string;
  id: keyof typeof SHORTCUTS_QUIZ_DEFAULT;
  handleSubmit: () => void;
}) => {
  const { setShowRepetitionSettings } = useQuizContext();
  const { shortcuts } = useQuizShortcutsContext();

  const colors = useColors();
  const isCorrect = btnType === 'correct';

  const shortcutTooltip = `${btnType} Shortcut key: ${shortcuts[id]}`;

  const calcRepetition = () => {
    const interval =
      repetitionStats.intervals[
        3 * (btnType === 'correct' ? 0 : 1) + (3 - confidence)
      ];

    return interval ? `${interval}d` : '';
  };

  return (
    <Flex flexDirection="column">
      <Button
        data-testid="flashcard.result.message"
        bg={isCorrect ? 'positive' : 'negative'}
        data-tip
        data-tooltip-id={`flashcard-control-button-${btnType}`}
        startIcon={
          isCorrect ? <Icon icon={faCheck} /> : <Icon icon={faTimes} />
        }
        minWidth="120px"
        onClick={handleSubmit}
      >
        {isCorrect ? (
          <FormattedMessage
            id="flashcard-controls.button.correct"
            defaultMessage="Got it"
          />
        ) : (
          <FormattedMessage
            id="flashcard-controls.button.incorrect"
            defaultMessage="Missed it"
          />
        )}
      </Button>
      <Tooltip
        id={`flashcard-control-button-${btnType}`}
        style={{
          backgroundColor: colors.gray80,
          color: 'white',
        }}
        place="top"
      >
        <P color="inherit" fontSize={13} sx={{ textTransform: 'capitalize' }}>
          {shortcutTooltip}
        </P>
      </Tooltip>
      <Flex
        data-testid={`${btnType}-repetition-modal`}
        onClick={() => setShowRepetitionSettings(true)}
        sx={{
          fontSize: 12,
          cursor: 'pointer',
          color: 'gray30',
          pt: '5px',
          justifyContent: 'center',
          ':hover': {
            color: 'gray40',
          },
        }}
      >
        <FormattedMessage
          id="flashcard-controls.repeats.time"
          defaultMessage="Repeats in {time}"
          values={{
            time:
              repetitionValue === 'osmosis'
                ? calcRepetition()
                : repetitionValue,
          }}
        />
      </Flex>
    </Flex>
  );
};
/**
 * FlashcardControls for Flashcard.
 */
const FlashcardControls = ({
  confidence,
  repetitionStats,
}: {
  confidence: QuizConfidence;
  repetitionStats: RepetitionStats;
}) => {
  const { get } = useFetch();

  const { data } = useQuery(
    'settingsModal',
    () => fetchRepetitionSettings(get),
    {
      refetchOnWindowFocus: false,
    },
  );
  const { quizSettings } = useQuizContext();
  const quizButtonsReversed = quizSettings[QuizSettings.BUTTONS_REVERSED];

  const { setOnCorrectnessKeyHandler } = useQuizItems();
  const { changeItem, submitAnswer } = useQuizContext();

  const handleSubmit = (isCorrect: boolean) => {
    submitAnswer(confidence, isCorrect);
    changeItem(1);
  };

  useEffect(() => {
    // Register the submit handler for the quiz buttons
    setOnCorrectnessKeyHandler(() => (isCorrect: boolean) => {
      handleSubmit(isCorrect);
    });

    return () => {
      setOnCorrectnessKeyHandler(undefined);
    };
  }, []);

  const correctKey = getRepetitionKey('correct', confidence);
  const correctRepetitionValue = (
    !!data && !!correctKey && typeof data[correctKey] !== 'boolean'
      ? data[correctKey]
      : '1m'
  ) as SettingType;

  const incorrectKey = getRepetitionKey('incorrect', confidence);
  const incorrectRepetitionValue = (
    !!data && !!incorrectKey && typeof data[incorrectKey] !== 'boolean'
      ? data[incorrectKey]
      : '1m'
  ) as SettingType;

  const flashcardControls: FlashcardControl[] = ['incorrect', 'correct'];

  return (
    <Flex
      justifyContent="center"
      flexDirection="column"
      sx={{
        mt: 4,
        borderRadius: '5px',
        width: '100%',
      }}
    >
      <Flex alignItems="center" width="100%">
        <Line />
        <Box mx={3} width="100%">
          <P
            width="100%"
            color="gray40"
            textAlign="center"
            fontWeight={700}
            fontSize={15}
          >
            <FormattedMessage
              id="flashcard-controls.message.how-did-you-do"
              defaultMessage="How did you do?"
            />
          </P>
        </Box>
        <Line />
      </Flex>
      <Flex justifyContent="center" mt={3} sx={{ gap: 3 }}>
        {(quizButtonsReversed
          ? flashcardControls.reverse()
          : flashcardControls
        ).map((control, i) => (
          <FlashcardControlsItem
            repetitionStats={repetitionStats}
            repetitionValue={
              control === 'incorrect'
                ? incorrectRepetitionValue
                : correctRepetitionValue
            }
            confidence={confidence}
            btnType={control}
            id={shortcutsIds[control]}
            handleSubmit={() => handleSubmit(control === 'correct')}
            key={i}
          />
        ))}
      </Flex>
      <ReverseQuizButtons />
    </Flex>
  );
};

export default FlashcardControls;
